import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import s from './PromtBlocks.module.css';
import Slider from '../../../../shared/ui/slider/Slider';
import { RgbaColorPicker } from 'react-colorful';
import { selectSelectedImage } from '../../../../store/selectors/imageSelectors';
import { commitHistory, updateSelectedImageSetting } from '../../../../store/slices/imageSlice';
import { useAppDispatch } from '../../../../store/store';
import { CollapsiblePanel } from '../../../../shared/ui/collapse-panel/CollapsiblePanel';

import ColorPicker, { useColorPicker } from 'react-best-gradient-color-picker';
import IconBrightness from '../../../../shared/ui/icons/IconBrightness';
import IconContrast from '../../../../shared/ui/icons/IconContrast';
import IconSaturation from '../../../../shared/ui/icons/IconSaturation';
import IconShadows from '../../../../shared/ui/icons/IconShadows';
import IconVignette from '../../../../shared/ui/icons/IconVignette';
import IconHighlights from '../../../../shared/ui/icons/IconHighlights';
import IconHue from '../../../../shared/ui/icons/IconHue';
import IconGrain from '../../../../shared/ui/icons/IconGrain';
import IconRedChannel from '../../../../shared/ui/icons/IconRedChannel';
import IconGreenChanell from '../../../../shared/ui/icons/IconGreenChanell';
import IconBlueChannel from '../../../../shared/ui/icons/IconBlueChannel';
import GradientEditor from './GradientEditor';

const sliderLabels = {
    brightness: 'Brightness',
    contrast: 'Contrast',
    saturation: 'Saturation',
    shadows: 'Shadows',
    vignette: 'Vignette',
    highlights: 'Highlights',
    // vibrance: 'Vibrance',
    // clarity: 'Clarity',
    hue: 'Hue',
    // blacks: 'Blacks',
    // white: 'White',
    grain: 'Grain',
    red: 'Red Channel',
    green: 'Green Channel',
    blue: 'Blue Channel',
} as const;

type SettingKey = keyof typeof sliderLabels;
type PanelKey = 'visual' | 'canvasColor' | 'transparency' | 'gradient';

const getSliderConfig = (key: SettingKey) => {
    if (key === 'hue') return { min: 0, max: 360, step: 1 };
    if (key === 'grain') return { min: 0, max: 100, step: 1 };
    if (key === 'vignette') return { min: 0, max: 100, step: 1 };
    if (key === 'red' || key === 'green' || key === 'blue') return { min: 0, max: 2, step: 0.01 };
    return { min: -100, max: 100, step: 1 };
};

const sliderIcons = {
    brightness: IconBrightness,
    contrast: IconContrast,
    saturation: IconSaturation,
    shadows: IconShadows,
    vignette: IconVignette,
    highlights: IconHighlights,
    hue: IconHue,
    grain: IconGrain,
    red: IconRedChannel,
    green: IconGreenChanell,
    blue: IconBlueChannel,
};

const AdjustVisualsBlock = () => {
    const dispatch = useAppDispatch();
    const selectedImage = useSelector(selectSelectedImage);
    const [expanded, setExpanded] = useState<PanelKey[]>([]);
    const [color, setColor] = useState<string>('#000000');

    if (!selectedImage?.image) return <p className={s.text}>No image selected</p>;

    const handleChangeVisualSettings = (key: SettingKey, value: number) => {
        dispatch(
            updateSelectedImageSetting({
                key: 'visual_settings' as any,
                value: { ...selectedImage?.image?.visual_settings, [key]: value },
            }),
        );
        dispatch(
            commitHistory({
                label: `color:${key}`,
                opts: { groupId: `color:${key}`, replace: true },
            }),
        );
    };

    const handleCanvasColorChange = (color: { r: number; g: number; b: number; a: number }) => {
        dispatch(
            updateSelectedImageSetting({
                key: 'canvas_color' as any,
                value: {
                    ...selectedImage?.image?.canvas_color,
                    red: color.r,
                    green: color.g,
                    blue: color.b,
                    transparency: color.a * 100,
                    // gradient: 'none',
                },
            }),
        );
    };
    const handleGradientChange = (color: string) => {
        setColor(color);
        // dispatch(
        //     updateTemporaryImageSetting({
        //         key: 'canvas_color' as any,
        //         value: {
        //             ...temporaryImage?.canvas_color,
        //             gradient: color,
        //         },
        //     }),
        // );
    };
    const toggle = (panel: PanelKey) => {
        setExpanded((prev) =>
            prev.includes(panel) ? prev.filter((p) => p !== panel) : [...prev, panel],
        );
    };

    return (
        <div className={s.container}>
            <div className={s.content}>
                <CollapsiblePanel
                    title="Visual Settings"
                    isOpen={expanded.includes('visual')}
                    onToggle={() => toggle('visual')}
                >
                    <div className={s.block_container}>
                        {Object.entries(sliderLabels).map(([key, label]) => {
                            const settingKey = key as SettingKey;
                            const { min, max, step } = getSliderConfig(settingKey);
                            const value = selectedImage?.image?.visual_settings?.[settingKey] ?? 0;
                            const Icon = sliderIcons[settingKey];

                            return (
                                <div key={settingKey} className={s.slider_item}>
                                    <div className={s.label_wrapper}>
                                        {Icon && (
                                            <div className={s.icon_wrapper}>
                                                <Icon width={15} height={15} />
                                            </div>
                                        )}
                                        <label
                                            className={s.label}
                                            style={{
                                                position: 'absolute',
                                                left: '20px',
                                                bottom: '0px',
                                            }}
                                        >
                                            {label}:
                                        </label>
                                        <span className={s.value}>{value}</span>
                                    </div>
                                    <Slider
                                        value={value}
                                        min={min}
                                        max={max}
                                        step={step}
                                        onChange={(newValue) =>
                                            handleChangeVisualSettings(settingKey, newValue)
                                        }
                                    />
                                </div>
                            );
                        })}
                    </div>
                </CollapsiblePanel>

                <CollapsiblePanel
                    title="Canvas Color"
                    isOpen={expanded.includes('canvasColor')}
                    onToggle={() => toggle('canvasColor')}
                >
                    <div className={s.block_container}>
                        <RgbaColorPicker
                            color={
                                selectedImage?.image?.canvas_color
                                    ? {
                                          r: selectedImage?.image?.canvas_color.red,
                                          g: selectedImage?.image?.canvas_color.green,
                                          b: selectedImage?.image?.canvas_color.blue,
                                          a: selectedImage?.image?.canvas_color.transparency / 100,
                                      }
                                    : { r: 255, g: 255, b: 255, a: 1 }
                            }
                            onChange={(color) => handleCanvasColorChange(color)}
                            style={{ width: '100%' }}
                        />
                    </div>
                </CollapsiblePanel>

                {/* <CollapsiblePanel
                    title="Gradient"
                    isOpen={expanded.includes('gradient')}
                    onToggle={() => toggle('gradient')}
                >
                    <div className={s.block_container}>
                        <GradientEditor value={color} onChange={handleGradientChange} />
                    </div>
                </CollapsiblePanel> */}
            </div>
        </div>
    );
};

export default AdjustVisualsBlock;
